import React, { useEffect, useRef, useState } from 'react';
import { wrap } from './RydTestbedWrap';
import { drawFrequencyCanvas, drawTimeCanvas } from '../render/spectrum';
import { getAudioContext } from '../audio/AudioController';

const EurydiceAudioCaptureTunerTestbed: React.FC = () => {
    const timeCanvasRef = useRef<HTMLCanvasElement>(null);
    const freqCanvasRef = useRef<HTMLCanvasElement>(null);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
    const [detectedNote, setDetectedNote] = useState<string>('');
    const [detectedFreq, setDetectedFreq] = useState<number>(0);

    // Helper: converts a frequency (Hz) into a musical note.
    const frequencyToNote = (frequency: number): string => {
        if (frequency <= 0) return '';
        const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F',
            'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const noteNumber = Math.round(12 * (Math.log(frequency / 440) / Math.log(2))) + 69;
        const octave = Math.floor(noteNumber / 12) - 1;
        const noteIndex = noteNumber % 12;
        return noteNames[noteIndex] + octave;
    };

    // Enumerate audio input devices once permission is granted.
    useEffect(() => {
        const getDevices = async () => {
            try {
                // Request permission by capturing audio temporarily.
                await navigator.mediaDevices.getUserMedia({
                    audio:{
                        echoCancellation: false,
                        noiseSuppression: false,
                        autoGainControl: false,

                    }});
                const allDevices = await navigator.mediaDevices.enumerateDevices();
                const audioDevices = allDevices.filter(device => device.kind === 'audioinput');
                setDevices(audioDevices);
                if (audioDevices.length && !selectedDeviceId) {
                    setSelectedDeviceId(audioDevices[0].deviceId);
                }
            } catch (err) {
                console.error('Error enumerating devices:', err);
            }
        };

        getDevices();
    }, [selectedDeviceId]);

    // Setup audio stream with the chosen microphone
    useEffect(() => {
        if (!selectedDeviceId) return;
        let mediaStream: MediaStream | null = null;
        let animationId: number;
        const audioContext = getAudioContext();

        const setupAudio = async () => {
            try {
                // audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                mediaStream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        deviceId: { exact: selectedDeviceId },
                        echoCancellation: false,
                        noiseSuppression: false,
                        autoGainControl: false
                     }
                });
                const source = audioContext.createMediaStreamSource(mediaStream);
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 8192;
                source.connect(analyser);
                const bufferLength = analyser.frequencyBinCount;
                const timeData = new Uint8Array(bufferLength);
                const freqData = new Uint8Array(bufferLength);

                const draw = () => {
                    analyser.getByteTimeDomainData(timeData);
                    analyser.getByteFrequencyData(freqData);

                    // Draw waveform
                    const timeCanvas = timeCanvasRef.current;
                    drawTimeCanvas(timeCanvas, timeData, bufferLength);

                    // Draw logarithmically scaled frequency bars
                    const freqCanvas = freqCanvasRef.current;
                    drawFrequencyCanvas(freqCanvas, freqData, bufferLength);

                    // Compute dominant frequency
                    if (audioContext) {
                        const sampleRate = audioContext.sampleRate;
                        let maxIndex = 0, maxValue = -Infinity;
                        for (let i = 1; i < freqData.length; i++) {
                            if (freqData[i] > maxValue) {
                                maxValue = freqData[i];
                                maxIndex = i;
                            }
                        }
                        // The frequency resolution is (sampleRate/2) / bufferLength.
                        const dominantFrequency = maxIndex * (sampleRate / 2) / bufferLength;
                        setDetectedFreq(dominantFrequency);
                        const noteName = frequencyToNote(dominantFrequency);
                        setDetectedNote(noteName);
                    }

                    animationId = requestAnimationFrame(draw);
                };

                draw();
            } catch (err) {
                console.error('Error setting up audio:', err);
            }
        };

        setupAudio();

        return () => {
            cancelAnimationFrame(animationId);
            if (mediaStream) {
                mediaStream.getTracks().forEach(track => track.stop());
            }
            if (audioContext) {
                audioContext.close();
            }
        };
    }, [selectedDeviceId]);

    return wrap(
        <div>
            <div style={{ marginBottom: '20px' }}>
                <label htmlFor="micSelect">Choose a microphone: </label>
                <select
                    id="micSelect"
                    value={selectedDeviceId}
                    onChange={e => setSelectedDeviceId(e.target.value)}
                >
                    {devices.map(device => (
                        <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${device.deviceId}`}
                        </option>
                    ))}
                </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
                <ul>
                <li><strong>Dominant Frequency:</strong> {detectedFreq.toFixed(2)} Hz</li>
                <li><strong>Note:</strong> {detectedNote}</li>
                </ul>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                <div>
                    <h3>Waveform</h3>
                    <canvas
                        ref={timeCanvasRef}
                        width={500}
                        height={200}
                        style={{ border: '1px solid #444' }}
                    />
                </div>
                <div>
                    <h3>Frequency Spectrum</h3>
                    <canvas
                        ref={freqCanvasRef}
                        width={500}
                        height={200}
                        style={{ border: '1px solid #444' }}
                    />
                </div>
            </div>


        </div>
    );
};

export default EurydiceAudioCaptureTunerTestbed;